import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Package,
  Clock,
  CheckCircle,
  XCircle,
  MapPin,
  Phone,
  Calendar,
  Store,
} from "lucide-react";
import axiosAdmin from "../axiosAdmin";
import usePlatformFees from "../../hooks/usePlatformFees";

export default function AdminOrderDetails() {
  const { orderNumber } = useParams();
  const [commande, setCommande] = useState(null);
  const [loading, setLoading] = useState(true);

  // Frais plateforme (lecture seule admin)
  const { serviceFee, deliveryFee, loading: loadingFees } = usePlatformFees();

  const getStatusConfig = (status) => {
    switch (status) {
      case "en_attente":
        return { label: "En attente", color: "bg-gray-100 text-gray-700", icon: Clock };
      case "en_preparation":
        return { label: "Préparation", color: "bg-orange-100 text-orange-700", icon: Package };
      case "prete":
        return { label: "Prête", color: "bg-green-100 text-green-700", icon: CheckCircle };
      case "livree":
        return { label: "Livrée", color: "bg-blue-100 text-blue-700", icon: CheckCircle };
      case "annulee":
        return { label: "Annulée", color: "bg-red-100 text-red-700", icon: XCircle };
      default:
        return { label: status, color: "bg-gray-100 text-gray-700", icon: Package };
    }
  };

  useEffect(() => {
    fetchCommande();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  const fetchCommande = async () => {
    try {
      const res = await axiosAdmin.get(`/orders/by-number/${orderNumber}`);
      setCommande(res.data.order);
    } catch (err) {
      console.error("❌ AdminOrderDetails:", err);
      setCommande(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="px-6 text-center text-gray-500 pt-28">
        Chargement des détails de la commande...
      </div>
    );
  }

  if (!commande) {
    return (
      <div className="max-w-3xl px-6 mx-auto pt-28">
        <p className="text-lg text-center text-gray-600">Commande introuvable</p>
        <Link
          to="/admin/orders"
          className="block mt-6 px-6 py-3 text-center text-white bg-[#f34141] rounded-xl font-semibold"
        >
          Retour aux commandes
        </Link>
      </div>
    );
  }

  const subtotal = commande.subtotal || 0;
  const finalTotal = subtotal + serviceFee + deliveryFee;

  const statusConfig = getStatusConfig(commande.status);
  const StatusIcon = statusConfig.icon;

  const merchantName =
    commande.merchantId?.name || commande.merchantName || "Supermarché";

  const paymentLabel =
    commande.paymentMethod === "cash"
      ? "Cash"
      : commande.paymentMethod === "mobile"
      ? "Paiement mobile"
      : commande.paymentMethod === "card"
      ? "Carte"
      : "—";

  const deliveryLabel =
    commande.fulfillmentType === "click_collect"
      ? "Click & Collect"
      : commande.fulfillmentType === "delivery"
      ? "Livraison"
      : "—";

  return (
    <div className="min-h-screen px-4 pb-32 font-sans pt-28 bg-gray-50">
      <div className="max-w-3xl mx-auto">

        <h1 className="mb-1 text-2xl font-extrabold text-gray-800">
          Détails de la commande (Admin)
        </h1>

        <p className="text-sm text-gray-500">
          Commande passée le{" "}
          {new Date(commande.createdAt).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>

        {/* INFOS */}
        <div className="p-5 mt-6 mb-6 bg-white border border-gray-100 shadow-lg rounded-2xl">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Commande #{commande.orderNumber}
              </h2>
              <p className="flex items-center mt-1 text-sm text-gray-500">
                <Store size={16} className="mr-1.5 text-gray-400" />
                {merchantName}
              </p>
            </div>

            <div
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${statusConfig.color}`}
            >
              <StatusIcon size={14} />
              <span>{statusConfig.label}</span>
            </div>
          </div>

          <div className="mt-4 space-y-2 text-sm text-gray-600">
            <p><strong>Mode de paiement :</strong> {paymentLabel}</p>
            <p><strong>Mode de réception :</strong> {deliveryLabel}</p>
          </div>

          {commande.paymentProof && (
            <div className="mt-4">
              <h3 className="mb-2 text-sm font-bold text-gray-700">
                Preuve de paiement
              </h3>
              <img
                src={commande.paymentProof} 
                alt="Preuve de paiement"
                className="w-full border rounded-xl shadow-sm"
                onError={(e) => {
                  // Sécurité au cas où l'URL serait mal formée ou ancienne
                  if (!commande.paymentProof.startsWith('http')) {
                    e.target.src = `https://souqi-api.onrender.com${commande.paymentProof.startsWith('/') ? '' : '/'}${commande.paymentProof}`;
                  }
                }}
              />
            </div>
          )}

          <div className="mt-4 space-y-3 text-sm text-gray-600">
            <p className="flex items-center">
              <Calendar size={18} className="mr-2 text-gray-400" />
              {new Date(commande.createdAt).toLocaleString("fr-FR")}
            </p>
            <p className="flex items-center">
              <Phone size={18} className="mr-2 text-gray-400" />
              {commande.customerPhone}
            </p>
            <p className="flex items-start">
              <MapPin size={18} className="mt-1 mr-2 text-gray-400" />
              {commande.address}
            </p>
          </div>
        </div>

        {/* ARTICLES */}
        <div className="p-5 mb-6 bg-white border border-gray-100 shadow-lg rounded-2xl">
          <h3 className="mb-4 text-xl font-bold text-gray-800">Articles</h3>

          {commande.items?.map((item, i) => (
            <div key={i} className="flex justify-between py-3 border-b last:border-b-0">
              <div>
                <p className="font-semibold">{item.name}</p>
                <p className="text-xs text-gray-500">Quantité : {item.quantity}</p>
              </div>
              <p className="font-bold">{item.price * item.quantity} MRU</p>
            </div>
          ))}
        </div>

        {/* RÉCAP */}
        <div className="p-5 mb-6 bg-white border border-gray-100 shadow-lg rounded-2xl">
          <h3 className="mb-4 text-xl font-bold text-gray-800">Récapitulatif</h3>

          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>Sous-total</span>
              <span>{subtotal} MRU</span>
            </div>

            {loadingFees ? (
              <p className="text-xs text-gray-400">Chargement des frais…</p>
            ) : (
              <>
                <div className="flex justify-between">
                  <span>Frais de service</span>
                  <span>{serviceFee} MRU</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>
                    Livraison <span className="italic">(Bientôt disponible)</span>
                  </span>
                  <span>{deliveryFee} MRU</span>
                </div>
              </>
            )}
          </div>

          <div className="pt-4 mt-4 border-t border-dashed">
            <div className="flex justify-between">
              <span className="text-xl font-bold">Total payé</span>
              <span className="text-3xl font-extrabold text-[#f34141]">
                {finalTotal} MRU
              </span>
            </div>
          </div>
        </div>
      </div>

      <Link
        to="/admin/orders"
        className="fixed bottom-0 left-0 right-0 max-w-3xl px-4 py-4 mx-auto border-t bg-gray-50"
      >
        <button className="w-full py-3 text-white bg-[#f34141] rounded-xl font-bold">
          Retour aux commandes
        </button>
      </Link>
    </div>
  );
}
